import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, X } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { fileToDataUrl, slugify } from "../../lib/admin";

const empty = { name: "", slug: "", brand_id: "", image: "", description: "" };

export default function AdminCategories() {
  const [cats, setCats] = useState([]);
  const [brands, setBrands] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () =>
    Promise.all([api.get("/categories"), api.get("/brands")]).then(([c, b]) => {
      setCats(c.data);
      setBrands(b.data);
    });
  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing("new");
    setForm({ ...empty, brand_id: brands[0]?.id || "" });
  };
  const openEdit = (c) => {
    setEditing(c.id);
    setForm({ ...empty, ...c });
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, slug: form.slug || slugify(form.name) };
      if (editing === "new") await api.post("/categories", payload);
      else await api.put(`/categories/${editing}`, payload);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete "${c.name}"?`)) return;
    await api.delete(`/categories/${c.id}`);
    toast.success("Deleted");
    load();
  };

  const onImage = async (file) => {
    if (!file) return;
    const url = await fileToDataUrl(file);
    setForm({ ...form, image: url });
  };

  const brandName = (id) => brands.find((b) => b.id === id)?.name || "—";

  return (
    <div data-testid="admin-categories-page">
      <div className="flex justify-between items-center mb-10">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">Catalog</div>
          <h1 className="font-display text-4xl tracking-tighter">Categories</h1>
        </div>
        <button onClick={openCreate} disabled={brands.length === 0} className="brand-btn px-5 py-2.5 text-xs uppercase tracking-[0.2em] inline-flex items-center gap-2 disabled:opacity-50" data-testid="category-create-btn">
          <Plus size={14} /> New Category
        </button>
      </div>

      {brands.length === 0 && <div className="text-zinc-500 text-sm mb-6">Create a brand first.</div>}

      <div className="border border-white/10 bg-[#0A0A0A]">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/10 text-left text-xs uppercase tracking-[0.2em] text-zinc-500">
              <th className="px-4 py-3 font-normal">Image</th>
              <th className="px-4 py-3 font-normal">Name</th>
              <th className="px-4 py-3 font-normal">Slug</th>
              <th className="px-4 py-3 font-normal">Brand</th>
              <th className="px-4 py-3 font-normal text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {cats.map((c) => (
              <tr key={c.id} className="border-b border-white/5" data-testid={`category-row-${c.slug}`}>
                <td className="px-4 py-3">
                  {c.image ? <img src={c.image} className="h-10 w-16 object-cover bg-[#141414]" alt="" /> : <div className="h-10 w-16 bg-[#141414]" />}
                </td>
                <td className="px-4 py-3 font-medium">{c.name}</td>
                <td className="px-4 py-3 text-zinc-400">{c.slug}</td>
                <td className="px-4 py-3 text-zinc-400">{brandName(c.brand_id)}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => openEdit(c)} className="text-zinc-400 hover:text-white p-2"><Edit2 size={14} /></button>
                  <button onClick={() => remove(c)} className="text-zinc-400 hover:text-red-400 p-2"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
            {cats.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-zinc-500">No categories yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4 sm:p-8" onClick={() => setEditing(null)}>
          <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl bg-[#0A0A0A] border border-white/10 p-6 my-8">
            <div className="flex justify-between items-center mb-6">
              <div className="font-display text-xl">{editing === "new" ? "New Category" : "Edit Category"}</div>
              <button type="button" onClick={() => setEditing(null)} className="text-zinc-400 hover:text-white"><X size={18} /></button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Name">
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value, slug: form.slug || slugify(e.target.value) })} className="adm-input" data-testid="category-form-name" />
              </Field>
              <Field label="Slug">
                <input required value={form.slug} onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })} className="adm-input" />
              </Field>
              <Field label="Brand">
                <select required value={form.brand_id} onChange={(e) => setForm({ ...form, brand_id: e.target.value })} className="adm-input" data-testid="category-form-brand">
                  <option value="">Select brand</option>
                  {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </Field>
              <Field label="Image (showcase background)">
                <div className="flex items-center gap-3">
                  {form.image && <img src={form.image} className="h-10 w-16 object-cover" alt="" />}
                  <input type="file" accept="image/*" onChange={(e) => onImage(e.target.files?.[0])} className="text-xs text-zinc-400" data-testid="category-form-image" />
                </div>
              </Field>
              <Field label="Description" full>
                <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="adm-input" data-testid="category-form-description" />
              </Field>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button type="button" onClick={() => setEditing(null)} className="px-5 py-2.5 text-xs uppercase tracking-[0.2em] border border-white/10 hover:bg-white/5">Cancel</button>
              <button type="submit" className="brand-btn px-5 py-2.5 text-xs uppercase tracking-[0.2em]" data-testid="category-form-save">Save</button>
            </div>
          </form>
        </div>
      )}
      <style>{`.adm-input { width: 100%; background: #141414; border: 1px solid rgba(255,255,255,0.1); padding: 0.625rem 0.875rem; font-size: 0.875rem; color: white; outline: none; } .adm-input:focus { border-color: rgba(255,255,255,0.3); }`}</style>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">{label}</label>
      {children}
    </div>
  );
}
